import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUpdateAbsence, type Absence } from "@/hooks/use-absences";
import { ABSENCE_TYPE_LABELS } from "@/lib/absence-display";

/**
 * VIB-1733: rett dato eller klokkeslett på et registrert fravær.
 *
 * Egen dialog i stedet for gjenbruk av `AbsenceForm`: skjemaet er bygget for
 * OPPRETTELSE (velg type, velg omfang, tøm feltene etterpå), mens redigering
 * har fast type og fast omfang. Å presse begge inn i samme komponent ville
 * gitt et skjema med to motstridende moduser.
 *
 * Type kan ikke endres — det gjøres ved å slette og registrere på nytt
 * (avklart med Erik 10.08).
 */
export function EditAbsenceDialog({
  absence,
  onOpenChange,
}: {
  absence: Absence | null;
  onOpenChange: (open: boolean) => void;
}) {
  const isPartial = Boolean(absence?.start_time && absence?.end_time);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [initialisedFor, setInitialisedFor] = useState<string | null>(null);
  const updateAbsence = useUpdateAbsence();

  // Fyll feltene når en ny rad åpnes. Nøkkelen på absence.id gjør at bytte
  // av rad uten å lukke dialogen også nullstiller riktig.
  if (absence && initialisedFor !== absence.id) {
    setInitialisedFor(absence.id);
    setFromDate(absence.start_date);
    setToDate(absence.end_date);
    setStartTime(absence.start_time?.slice(0, 5) ?? "");
    setEndTime(absence.end_time?.slice(0, 5) ?? "");
  }

  if (!absence) return null;

  const invalid = isPartial
    ? !fromDate || !startTime || !endTime || endTime <= startTime
    : !fromDate || !toDate || toDate < fromDate;

  const handleSave = () => {
    if (invalid) return;
    updateAbsence.mutate(
      {
        id: absence.id,
        user_id: absence.user_id,
        type: absence.type,
        start_date: fromDate,
        end_date: isPartial ? fromDate : toDate,
        start_time: isPartial ? startTime : null,
        end_time: isPartial ? endTime : null,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Endre {ABSENCE_TYPE_LABELS[absence.type].toLowerCase()}
          </DialogTitle>
        </DialogHeader>

        {isPartial ? (
          <div className="grid gap-3 min-[24rem]:grid-cols-3">
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Dato</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-11 w-full bg-background text-base"
                aria-label="Dato"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Fra</span>
              <Input
                type="time"
                step={900}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-11 w-full bg-background text-base"
                aria-label="Fra tidspunkt"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Til</span>
              <Input
                type="time"
                step={900}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="h-11 w-full bg-background text-base"
                aria-label="Til tidspunkt"
              />
            </label>
          </div>
        ) : (
          <div className="grid gap-3 min-[24rem]:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Fra dato</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-11 w-full bg-background text-base"
                aria-label="Fra dato"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Til dato</span>
              <Input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                className="h-11 w-full bg-background text-base"
                aria-label="Til dato"
              />
            </label>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Type kan ikke endres. Skal fraværet bytte mellom syk og ferie, slett
          det og registrer på nytt.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSave} disabled={invalid || updateAbsence.isPending}>
            {updateAbsence.isPending ? "Lagrer…" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
